import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { animate, motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./ReclaimedPaywall.module.scss"

/**
 * ReclaimedPaywall — an original, self-contained, NON-privacy paywall.
 *
 * Divergence brief (see claude-paywalls/_ledger.md). The prior twelve paywalls
 * clustered hard; this one moves every axis off the worn path:
 *   - Value frame:   TIME-SAVED — "stop losing your weekends to photo busywork",
 *                    a frame no prior paywall used.
 *   - Layout:        a true horizontal PAGER / swipe deck of full-bleed photos —
 *                    not a centered hero + glowing card.
 *   - Interaction:   INPUT-DRIVEN — swiping the deck fills a "time bank": a live
 *                    counter at the top counts the hours given back as you go.
 *   - Register:      GENEROUS / bright, golden-hour optimism.
 *   - Art direction: LIGHT + PHOTOGRAPHIC, editorial type. No dark theme, no
 *                    skeuomorphism, no particles, no glowing card.
 *
 * The pitch: CloudVault backs up, finds and organizes your photos automatically,
 * so the ~3 days a year you'd otherwise spend on photo chores become yours
 * again. Each panel trades a chore for the moment you could spend instead, and
 * the hours add up at the top. Yearly is the prominent price and is dockable
 * from any panel.
 */

interface Panel {
  img: string
  hours: number
  chore: string
  kicker: string
  title: string
  body: string
}

// Honest, modest per-task estimates. They sum to the headline total.
const PANELS: Panel[] = [
  {
    img: "/reclaimed/coffee.jpg",
    hours: 14,
    chore: "Backing photos up by hand",
    kicker: "It just backs up",
    title: "Slow mornings, not sync screens",
    body: "Every shot is safely backed up the second you take it — full quality, nothing to plug in or remember.",
  },
  {
    img: "/reclaimed/hike.jpg",
    hours: 22,
    chore: "Scrolling for that one photo",
    kicker: "Found in a word",
    title: "Be there, not in your camera roll",
    body: "Search “sunset” or “Maya” and it surfaces in a heartbeat. No more thumb-scrolling back through three years.",
  },
  {
    img: "/reclaimed/dinner.jpg",
    hours: 18,
    chore: "Clearing “Storage Full”",
    kicker: "Room, always",
    title: "Space for the moment in front of you",
    body: "Originals live safely in the cloud at full quality, so your phone keeps room for the next memory — your photos stay yours.",
  },
  {
    img: "/reclaimed/reading.jpg",
    hours: 17,
    chore: "Sorting and tidying albums",
    kicker: "Sorts itself",
    title: "An afternoon that's actually yours",
    body: "Albums build and organize themselves in the background, so the tidying you keep meaning to do is simply already done.",
  },
]

const TOTAL_HOURS = PANELS.reduce((s, p) => s + p.hours, 0) // 71

// cumulative hours "banked" by the time you've reached panel i (0 = intro)
const CUMULATIVE = PANELS.reduce<number[]>(
  (acc, p) => [...acc, (acc[acc.length - 1] ?? 0) + p.hours],
  [0],
)

const ReclaimedPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const trackRef = useRef<HTMLDivElement>(null)
  const hoursRef = useRef<HTMLSpanElement>(null)
  const shownHours = useRef(0)
  const [index, setIndex] = useState(0)

  // total panels = intro + benefits + plan
  const PANEL_COUNT = PANELS.length + 2
  const PLAN_INDEX = PANEL_COUNT - 1

  const setHoursDisplay = useCallback(
    (target: number) => {
      const el = hoursRef.current
      if (!el) return
      if (reduceMotion) {
        el.textContent = String(target)
        shownHours.current = target
        return
      }
      animate(shownHours.current, target, {
        duration: 0.55,
        ease: [0.22, 1, 0.36, 1],
        onUpdate: (v) => {
          el.textContent = String(Math.round(v))
        },
      })
      shownHours.current = target
    },
    [reduceMotion],
  )

  // Drive active index + time-bank from horizontal scroll position.
  const onScroll = useCallback(() => {
    const track = trackRef.current
    if (!track) return
    const w = track.clientWidth || 1
    const i = Math.round(track.scrollLeft / w)
    setIndex((prev) => (prev === i ? prev : i))
  }, [])

  useEffect(() => {
    // index 0 = intro (0h). index 1..N maps to CUMULATIVE[1..N]. plan = full.
    const banked =
      index >= PLAN_INDEX
        ? TOTAL_HOURS
        : CUMULATIVE[Math.min(index, PANELS.length)]
    setHoursDisplay(banked)
  }, [index, PLAN_INDEX, setHoursDisplay])

  const goTo = useCallback((i: number) => {
    const track = trackRef.current
    if (!track) return
    track.scrollTo({ left: i * track.clientWidth, behavior: "smooth" })
  }, [])

  const progress = useMemo(
    () => Math.min(1, index / (PANEL_COUNT - 1)),
    [index, PANEL_COUNT],
  )

  const handleClose = () => native.postNativeEvent("close")
  const buyYearly = () => {
    if (yearlyProduct) native.subscribe(yearlyProduct)
  }
  const buyMonthly = () => {
    if (monthlyProduct) native.subscribe(monthlyProduct)
  }

  const onTrack = index < PLAN_INDEX

  return (
    <div className={styles.root}>
      {/* fixed top: brand + live time bank + close */}
      <header
        className={`${styles.topbar} ${
          index >= PLAN_INDEX ? styles.topbarOnLight : ""
        }`}
      >
        <div className={styles.brand}>
          <span className={styles.brandMark}>Reclaimed</span>
          <span className={styles.brandSub}>by CloudVault</span>
        </div>

        <div className={styles.bank} aria-live="polite">
          <span className={styles.bankNum}>
            <span ref={hoursRef}>0</span>
            <span className={styles.bankUnit}>h</span>
          </span>
          <span className={styles.bankLabel}>back / year</span>
        </div>

        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
            <path
              d="M5 5l14 14M19 5L5 19"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      {/* progress hairline */}
      <div className={styles.progressTrack}>
        <motion.div
          className={styles.progressFill}
          animate={{ scaleX: progress }}
          transition={{ type: "spring", stiffness: 220, damping: 30 }}
        />
      </div>

      {/* horizontal swipe deck */}
      <div
        className={styles.track}
        ref={trackRef}
        onScroll={onScroll}
        role="group"
        aria-label="Reclaimed — swipe to see the time you get back"
      >
        {/* INTRO */}
        <section className={`${styles.panel} ${styles.intro}`}>
          <div
            className={styles.introPhoto}
            style={{ backgroundImage: "url(/reclaimed/beach.jpg)" }}
          />
          <div className={styles.introScrim} />
          <div className={styles.introInner}>
            <p className={styles.eyebrow}>A year of photos · a year of admin</p>
            <h1 className={styles.introTitle}>
              You lose about
              <br />
              <em>three days a year</em>
              <br />
              just minding your photos.
            </h1>
            <p className={styles.introBody}>
              Backing up. Hunting for one shot. Clearing space. Tidying albums.
              CloudVault does all of it for you. Swipe to take the time back.
            </p>
            <button className={styles.swipeHint} onClick={() => goTo(1)}>
              See what {TOTAL_HOURS}h buys
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                aria-hidden="true"
              >
                <path
                  d="M5 12h13M13 6l6 6-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
            </button>
          </div>
        </section>

        {/* BENEFIT PANELS */}
        {PANELS.map((p, i) => (
          <section className={styles.panel} key={p.img}>
            <div
              className={styles.photo}
              style={{ backgroundImage: `url(${p.img})` }}
            />
            <div className={styles.photoScrim} />

            <div className={styles.hoursStamp}>
              <span className={styles.hoursStampNum}>+{p.hours}</span>
              <span className={styles.hoursStampUnit}>hours / year</span>
            </div>

            <div className={styles.caption}>
              <span className={styles.kicker}>{p.kicker}</span>
              <h2 className={styles.captionTitle}>{p.title}</h2>
              <p className={styles.captionBody}>{p.body}</p>
              <div className={styles.tradeRow}>
                <span className={styles.tradeFrom}>{p.chore}</span>
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  aria-hidden="true"
                  className={styles.tradeArrow}
                >
                  <path
                    d="M5 12h13M13 6l6 6-6 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
                <span className={styles.tradeTo}>handled for you</span>
              </div>
            </div>
          </section>
        ))}

        {/* PLAN */}
        <section className={`${styles.panel} ${styles.plan}`}>
          <div className={styles.planInner}>
            <p className={styles.planEyebrow}>Your time bank</p>
            <div className={styles.planTotal}>
              <span className={styles.planTotalNum}>{TOTAL_HOURS}</span>
              <span className={styles.planTotalUnit}>
                hours
                <br />a year
              </span>
            </div>
            <p className={styles.planLede}>
              Almost three full days back — every year — for everything that
              isn't your camera roll.
            </p>

            <button className={styles.cta} onClick={buyYearly}>
              <span className={styles.ctaTop}>Get your time back</span>
              <span className={styles.ctaSub}>
                {yearlyPrice} / year · just {yearlyPerMonth} / mo
              </span>
            </button>

            <button className={styles.monthlyLink} onClick={buyMonthly}>
              or pay monthly at {monthlyPrice} / mo
            </button>

            <p className={styles.legal}>
              Cancel anytime. Your photos always stay yours, even if you cancel.
            </p>
            <div className={styles.legalLinks}>
              <button onClick={() => native.postNativeEvent("terms")}>
                Terms
              </button>
              <span>·</span>
              <button onClick={() => native.postNativeEvent("privacy")}>
                Privacy
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* dots */}
      <div className={styles.dots}>
        {Array.from({ length: PANEL_COUNT }).map((_, i) => (
          <button
            key={i}
            className={`${styles.dot} ${i === index ? styles.dotOn : ""}`}
            onClick={() => goTo(i)}
            aria-label={`Go to panel ${i + 1}`}
          />
        ))}
      </div>

      {/* persistent dock CTA — visible while browsing the deck */}
      <motion.div
        className={styles.dock}
        initial={false}
        animate={{
          y: onTrack ? 0 : 120,
          opacity: onTrack ? 1 : 0,
        }}
        transition={{ type: "spring", stiffness: 260, damping: 30 }}
        style={{ pointerEvents: onTrack ? "auto" : "none" }}
      >
        <div className={styles.dockPrice}>
          <span className={styles.dockYear}>{yearlyPrice}</span>
          <span className={styles.dockYearUnit}>/ year</span>
          <span className={styles.dockPerMo}>just {yearlyPerMonth}/mo</span>
        </div>
        <button className={styles.dockBtn} onClick={buyYearly}>
          Get your time back
        </button>
      </motion.div>
    </div>
  )
}

export default ReclaimedPaywall
